import { useCallback, useState } from 'react';
import type { EvinAnalysisResult, AnalysisType } from '../../shared/types';

interface Props {
  result: EvinAnalysisResult;
  analysisType?: AnalysisType;
}

const VERDICT_LABELS: Record<string, string> = {
  CONFORME: 'Conforme',
  VIGILANCE: 'Vigilance',
  RISQUE_ELEVE: 'Risque élevé',
  NON_CONFORME: 'Non conforme',
};

const ANALYSIS_TYPE_LABELS: Record<string, string> = {
  brand_name: 'Nom de marque',
  slogan: 'Slogan',
  concept: 'Concept',
  label: 'Étiquette',
  campaign: 'Campagne',
  social_post: 'Post social',
  creative_brief: 'Brief créatif',
  general_question: 'Question générale',
};

// Article 22 RGPD — canal de recours humain visible.
// Volontairement positionné comme un « signalement qualité produit » et non
// comme une « contestation juridique » pour ne pas créer d'attente de conseil
// juridique gratuit que l'éditeur (non avocat) ne peut ni n'a vocation à
// fournir. Voir docs/compliance/templates-reponse-signalements.md pour les
// modèles de réponse à utiliser à la réception d'un signalement.

export default function ReportErrorButton({ result, analysisType }: Props) {
  const [open, setOpen] = useState(false);

  const handleOpenMail = useCallback(() => {
    const verdictLabel = VERDICT_LABELS[result.verdict] ?? result.verdict;
    const typeLabel = analysisType ? ANALYSIS_TYPE_LABELS[analysisType] ?? analysisType : 'Analyse';
    const subject = `Signalement Evinly — ${typeLabel} (${verdictLabel}, ${result.score}/100)`;

    const body = [
      'Bonjour,',
      '',
      "Je souhaite vous signaler ce qui me semble être une erreur ou une imprécision dans l'analyse ci-dessous. J'ai conscience que ce signalement constitue un retour qualité produit et ne donnera pas lieu à un avis juridique formel.",
      '',
      "— Détails de l'analyse —",
      `Type : ${typeLabel}`,
      `Verdict : ${verdictLabel}`,
      `Score global : ${result.score}/100`,
      `Score Défense : ${result.judgeDefenseScore}/100`,
      `Score Contradicteur : ${result.judgeContradicteurScore}/100`,
      result.summary ? `Résumé : ${result.summary}` : null,
      '',
      '— Élément analysé —',
      result.originalInput ? result.originalInput : "[Le contenu original n'est pas joint automatiquement à ce message. Si vous souhaitez le partager, copiez-le ci-dessous.]",
      '',
      '— Ce qui me semble erroné —',
      "[Précisez ici quel élément du verdict ou du raisonnement vous semble incorrect, et pourquoi. Plus votre description est précise, plus le retour de l'équipe Evinly sera utile.]",
      '',
      'Cordialement,',
    ].filter(Boolean).join('\n');

    const mailto = `mailto:privacy@blackthornsdesign.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  }, [result, analysisType]);

  return (
    <div className="mt-4 rounded-xl border border-slate-200 dark:border-slate-800 shader:border-white/10 bg-white dark:bg-slate-900/40 shader:bg-white/5 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 shader:hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 shader:bg-white/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-slate-600 dark:text-slate-300 shader:text-white/70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100 shader:text-white">
              Signaler une erreur d'analyse
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/50">
              Aidez-nous à améliorer Evinly
            </p>
          </div>
        </div>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100 dark:border-slate-800/50 shader:border-white/5">
          <p className="text-xs text-slate-600 dark:text-slate-400 shader:text-white/60 leading-relaxed mb-3">
            Vous pensez que notre pipeline d'IA a mal analysé votre contenu, mal interprété un texte de loi, ou produit un verdict incohérent ? Signalez-le-nous : votre retour est précieux pour améliorer la qualité du service. Conformément à l'article 22 du RGPD, chaque signalement fait l'objet d'un examen humain.
          </p>
          <p className="text-xs text-slate-600 dark:text-slate-400 shader:text-white/60 leading-relaxed mb-3">
            <strong>Important :</strong> notre retour portera uniquement sur les éléments techniques pris en compte par notre pipeline. Il ne constitue ni un avis juridique, ni une validation de conformité. Pour toute validation juridique formelle de votre contenu, consultez un avocat spécialisé.
          </p>
          <p className="text-xs text-slate-600 dark:text-slate-400 shader:text-white/60 leading-relaxed mb-3">
            En cliquant sur le bouton ci-dessous, votre client mail s'ouvrira avec un signalement pré-rempli adressé à <span className="font-mono text-slate-700 dark:text-slate-300">privacy@blackthornsdesign.com</span>. Vous pourrez compléter ce qui vous semble erroné avant l'envoi.
          </p>
          <button
            onClick={handleOpenMail}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            Envoyer un signalement
          </button>
        </div>
      )}
    </div>
  );
}
